import React, { useEffect, useRef, useState } from 'react'

interface LocationPickerProps {
  latitude: string
  longitude: string
  locationName: string
  onLocationSelect: (lat: number, lng: number, name: string) => void
  disabled?: boolean
}

declare global {
  interface Window {
    google: any
    initMap: () => void
  }
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  latitude,
  longitude,
  locationName,
  onLocationSelect,
  disabled = false
}) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string>('')

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

  // Default to Bishkek, Kyrgyzstan
  const defaultLat = latitude ? parseFloat(latitude) : 42.8746
  const defaultLng = longitude ? parseFloat(longitude) : 74.5698

  useEffect(() => {
    console.log('[LocationPicker] GOOGLE_MAPS_API_KEY:', GOOGLE_MAPS_API_KEY)
    console.log('[LocationPicker] process.env.REACT_APP_GOOGLE_MAPS_API_KEY:', process.env.REACT_APP_GOOGLE_MAPS_API_KEY)
    console.log('[LocationPicker] All env keys:', Object.keys(process.env))

    if (!GOOGLE_MAPS_API_KEY) {
      setError('Google Maps API key not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.')
      setIsLoading(false)
      return
    }

    const loadGoogleMaps = () => {
      if (window.google && window.google.maps) {
        initializeMap()
        return
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]')
      if (existingScript) {
        window.initMap = initializeMap
        return
      }

      // Устанавливаем глобальный callback для Google Maps
      window.initMap = initializeMap

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=initMap&libraries=marker&v=beta`
      script.addEventListener('error', () => {
        setError('Failed to load Google Maps')
        setIsLoading(false)
      })
      document.head.appendChild(script)
    }

    const initializeMap = () => {
      if (!mapRef.current || !window.google) return

      try {
        const mapOptions = {
          center: { lat: defaultLat, lng: defaultLng },
          zoom: 12,
          mapId: 'd03f114092eb52d4c96d499f',
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          gestureHandling: disabled ? 'none' : 'greedy',
          draggable: !disabled,
          clickableIcons: !disabled
        }

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, mapOptions)

        // Add marker using new AdvancedMarkerElement
        markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat: defaultLat, lng: defaultLng },
          map: mapInstanceRef.current,
          gmpDraggable: !disabled,
          title: locationName || 'Event Location'
        })

        if (!disabled) {
          // Add click listener to map
          mapInstanceRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return
            const lat = e.latLng.lat()
            const lng = e.latLng.lng()
            updateLocation(lat, lng)
          })

          // Add drag listener to marker
          markerRef.current.addListener('dragend', (e: google.maps.MapMouseEvent) => {
            if (!e.latLng) return
            const lat = e.latLng.lat()
            const lng = e.latLng.lng()
            updateLocation(lat, lng)
          })
        }

        setIsLoading(false)
        setError('')
      } catch (err) {
        setError('Failed to initialize map')
        setIsLoading(false)
      }
    }

    const updateLocation = (lat: number, lng: number) => {
      // Update marker position (AdvancedMarkerElement uses position property)
      if (markerRef.current) {
        markerRef.current.position = { lat, lng }
      }

      // Center map on new location
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter({ lat, lng })
      }

      // Только координаты, название пользователь вводит вручную
      onLocationSelect(lat, lng, locationName)
    }

    loadGoogleMaps()

    return () => {
      // Cleanup (AdvancedMarkerElement uses map property)
      if (markerRef.current) {
        markerRef.current.map = null
      }
    }
  }, [GOOGLE_MAPS_API_KEY, defaultLat, defaultLng, disabled, onLocationSelect, locationName])

  if (error) {
    return (
      <div className="w-full h-[400px] bg-[#fff7e6] border border-[#ffd591] rounded-lg flex flex-col items-center justify-center gap-3 p-5 text-center">
        <div className="text-[32px]">⚠️</div>
        <div className="text-sm text-[#fa8c16] font-medium">
          {error}
        </div>
        {!GOOGLE_MAPS_API_KEY && (
          <div className="text-[13px] text-black/65 max-w-[500px]">
            To enable map functionality, add your Google Maps API key to the .env file:
            <br />
            <code className="bg-[#f5f5f5] px-1.5 py-0.5 rounded mt-2 inline-block">
              REACT_APP_GOOGLE_MAPS_API_KEY=your_api_key_here
            </code>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div ref={mapRef} className="w-full h-[400px] rounded-lg border border-[#d9d9d9] overflow-hidden" />
      {isLoading && (
        <div className="absolute inset-0 bg-white/90 flex items-center justify-center rounded-lg">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      )}
      {latitude && longitude && (
        <div className="absolute bottom-4 left-4 bg-white px-3 py-2 rounded-md shadow-md text-[13px] text-black/85 max-w-[calc(100%-32px)]">
          <div className="font-medium mb-0.5">
            {locationName || 'Выбранная локация'}
          </div>
          <div className="text-black/45 text-xs">
            {parseFloat(latitude).toFixed(6)}, {parseFloat(longitude).toFixed(6)}
          </div>
        </div>
      )}
      {!disabled && !isLoading && (
        <div className="absolute top-4 left-4 bg-white px-3 py-2 rounded-md shadow-md text-xs text-black/65 max-w-[200px]">
          💡 Кликните на карту или перетащите маркер
        </div>
      )}
    </div>
  )
}
